import { TEDEKKontodelkombinationKOMBINATIONEnum } from '../../../../../globalQueryTypes';
import { capitalize } from '../../../../../Utils/stringUtils';
import {
  IKodstrang,
  IKodstrangDel,
  IKonteringValidationError,
} from './KonteringManager';
import {
  IKontodel,
  IKontodelDef,
  IKontoplan,
} from './Query/KonteringManagerQuery';

const isKontodelDefVisible = (
  kontodelDef: IKontodelDef | null | undefined,
  fakturaBokfDate: Date
) => {
  if (kontodelDef != null) {
    return (
      kontodelDef.visibleFromDate <= fakturaBokfDate &&
      kontodelDef.visibleToDate >= fakturaBokfDate
    );
  }
  return false;
};

export const getKontodelByKontodelsdef = (
  kontodelDefs: IKontodelDef[],
  ordningsNr: number,
  kontodel: string,
  date: Date
) => {
  return (
    kontodelDefs
      .find(def => def.ordningsNr === ordningsNr)
      ?.kontodelar.find(
        kd => kd.kontodel === kontodel && isVisibleByKontodelDate(kd, date)
      ) ?? null
  );
};

export const getKontodelByIDInKontodelsdef = (
  kontodelDefs: IKontodelDef[],
  ordningsNr: number,
  kontodelID: string,
  date: Date
) => {
  return (
    kontodelDefs
      .find(def => def.ordningsNr === ordningsNr)
      ?.kontodelar.find(kd => {
        return kd.id === kontodelID && isVisibleByKontodelDate(kd, date);
      }) ?? null
  );
};

export const isVisibleByKontodelDate = (
  kontodel: IKontodel,
  date: Date
): boolean => {
  return kontodel.franDatum <= date && kontodel.tillDatum >= date;
};

export const kombinationValidation = (
  kodstrang: IKodstrang,
  kontoplan: IKontoplan,
  bokfDate: Date,
  kodstrangDel: IKodstrangDel,
  kontodelDefs: IKontodelDef[],
  kontodel: IKontodel | null,
  isMottagningsAttestant?: boolean
): IKonteringValidationError[] => {
  const kombinationErrors: IKonteringValidationError[] = [];
  if (!kontodelDefs || !kodstrangDel.id) {
    return kombinationErrors;
  }

  const kontodelDef = kontoplan.kontodelDefs.find(
    kd => kd.ordningsNr === kodstrangDel.ordningsNr
  );

  kontodel?.kombinationer.forEach((kombination: any) => {
    const kombineradKontodel = kodstrang.kontodelar.find(
      k => k.ordningsNr === kombination.kombOrdningsNr
    );

    const kombKoddelDef = kontoplan.kontodelDefs.find(
      kd => kd.ordningsNr === kombination.kombOrdningsNr
    );
    const kombBen = kombKoddelDef?.namn;

    if (
      (kombination.kombination ===
        TEDEKKontodelkombinationKOMBINATIONEnum.MASTE_KOMBINERAS ||
        kombination.lokaltKrav) &&
      !kombineradKontodel?.id &&
      isKontodelDefVisible(kombKoddelDef, bokfDate)
    ) {
      kombinationErrors.push({
        kontodel: kodstrangDel.kontodel,
        lopnr: kombKoddelDef?.ordningsNr ?? 1,
        meddelande: `${kombBen} måste anges för ${kontodelDef?.namn.toLocaleLowerCase()} ${
          kodstrangDel.kontodel
        } ${kodstrangDel.benamning}`,
        type: isMottagningsAttestant ? 'Warning' : 'Error',
      });
    } else if (
      kombination.kombination ===
        TEDEKKontodelkombinationKOMBINATIONEnum.FAR_EJ_KOMBINERAS &&
      kombineradKontodel?.id
    ) {
      kombinationErrors.push({
        kontodel: kodstrangDel.kontodel,
        lopnr: kombKoddelDef?.ordningsNr ?? 1,
        meddelande: `${kombBen} får ej kombineras med ${kontodelDef?.namn.toLocaleLowerCase()} ${
          kodstrangDel.kontodel
        } ${kodstrangDel.benamning}`,
        type: 'Error',
      });
    }
  });

  return kombinationErrors;
};

export const kontodelValidation = (
  kodstrangDel: IKodstrangDel,
  kontodel: IKontodel | null,
  BFO: boolean = false
): IKonteringValidationError[] => {
  const kontodelErrors: IKonteringValidationError[] = [];
  if (!BFO && kontodel?.sparrAttest) {
    kontodelErrors.push({
      kontodel: kodstrangDel.kontodel,
      lopnr: kodstrangDel.ordningsNr,
      meddelande: 'Konto spärrat för attest',
      type: 'Error',
    });
  }

  if (BFO && kontodel?.sparraManuell) {
    kontodelErrors.push({
      kontodel: kodstrangDel.kontodel,
      lopnr: kodstrangDel.ordningsNr,
      meddelande: 'Konto spärrat för manuell bokföring',
      type: 'Error',
    });
  }

  if (kontodel?.rubrikKonto) {
    kontodelErrors.push({
      kontodel: kodstrangDel.kontodel,
      lopnr: kodstrangDel.ordningsNr,
      meddelande: 'Konto är ett rubrikkonto',
      type: 'Error',
    });
  }

  if (kontodel?.budgetKonto) {
    kontodelErrors.push({
      kontodel: kodstrangDel.kontodel,
      lopnr: kodstrangDel.ordningsNr,
      meddelande: 'Konto är ett budgetkonto',
      type: 'Error',
    });
  }

  if (kontodel?.aktiv === false || kontodel == null) {
    const name = capitalize(kodstrangDel.benamning);
    kontodelErrors.push({
      kontodel: kodstrangDel.kontodel,
      lopnr: kodstrangDel.ordningsNr,
      meddelande: `Konto ${kodstrangDel.kontodel}  ${name} är inte aktivt i kodplanen`,
      type: 'Error',
      showAsHint: false,
    });
  }

  return kontodelErrors;
};

export const kombinationsVillkorValidation = (
  kodstrang: IKodstrang,
  kontoplan: IKontoplan,
  bokfDate: Date,
  kodstrangDel: IKodstrangDel,
  kombVillkorOK: boolean,
  kontodel: IKontodel | null,
  kontodelDef: IKontodelDef | null,
  kombKoddelarDefs: IKontodelDef[],
  isMottagningsAttestant?: boolean
): IKonteringValidationError[] => {
  let kombinationVillkorErrors: IKonteringValidationError[] = [];
  let localKombVillkor = kombVillkorOK;

  if (kodstrangDel.kontodel === 'Saknas') {
    return kombinationVillkorErrors;
  }

  if (
    kodstrangDel.kontodel !== 'Saknas' &&
    (kontodel == null || !kontodel.aktiv)
  ) {
    return kombinationVillkorErrors;
  }

  for (
    let kombOrdningsNr = 1;
    kombOrdningsNr <= kontoplan.kontodelDefs.length;
    kombOrdningsNr++
  ) {
    if (kombOrdningsNr !== kodstrangDel.ordningsNr) {
      localKombVillkor =
        kontodel?.kombinationsVillkor.filter(
          kd => kd.kombOrdningsNr === kombOrdningsNr
        ).length === 0;
      const kombineradKontodel = kodstrang.kontodelar.find(
        k => k.ordningsNr === kombOrdningsNr
      );
      kontodel?.kombinationsVillkor
        .filter(kd => kd.kombOrdningsNr === kombOrdningsNr)
        .forEach(kd => {
          const fromKontodel = kd.fromKontodel;
          const toKontodel = kd.toKontodel;

          const actualKontodel = kombineradKontodel?.kontodel ?? '';

          localKombVillkor =
            localKombVillkor ||
            (actualKontodel >= fromKontodel && actualKontodel <= toKontodel);
        });

      const kombKoddelDef = kombKoddelarDefs[kombOrdningsNr - 1];

      if (!localKombVillkor && isKontodelDefVisible(kombKoddelDef, bokfDate)) {
        const kombBen = kombKoddelDef?.namn;

        const error: IKonteringValidationError = !kombineradKontodel?.kontodel
          ? {
              kontodel: kodstrangDel.kontodel,
              lopnr: kombKoddelDef?.ordningsNr ?? 1,
              meddelande: `${kombBen} måste anges för ${kontodelDef?.namn.toLocaleLowerCase()} ${
                kontodel?.kontodel
              } ${kodstrangDel.benamning}`,
              type: isMottagningsAttestant ? 'Warning' : 'Error',
            }
          : {
              kontodel: kodstrangDel.kontodel,
              lopnr: kombKoddelDef?.ordningsNr ?? 1,
              meddelande: `${kombBen} ${
                kombineradKontodel?.kontodel
              } får ej kombineras med ${kontodelDef?.namn.toLocaleLowerCase()} ${
                kontodel?.kontodel
              } ${kodstrangDel.benamning}`,
              type: 'Error',
            };

        kombinationVillkorErrors.push(error);
      }
    }
  }

  return kombinationVillkorErrors;
};
